import React, { useEffect, useRef, useState } from 'react'
import { OpportunityDetails } from '../../types'
import { InputModal } from '../InputModal'
import { Card } from '../Card'
import { TermsheetAgreementForm } from './termsheet-agreement-modal-form'
import { documentServices, investServices } from '../../../services'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Flex } from '../Flex'
import {
  buildTermsheetPdfFileName,
  exportTermsheetHtmlToPdf,
  TERMSHEET_SIGNATURE_IMAGE_URL,
} from '../../utils/termsheet-pdf-export'
import {
  parseTermsheetAgreementPollResponse,
  TERMSHEET_POLL_INTERVAL_MS,
} from '../../utils/termsheet-agreement'
import { EngagementEvents, logCrmEvent } from '../../utils/crmEvents'
import { PendingIcon } from '../Icon'
import { SignNowEmbedModal } from '../SignNowEmbedModal'

interface TermsheetAgreementProps {
  active: boolean
  handleOnClose: () => void
  opportunity: OpportunityDetails
  investmentAmount: string
  investmentId: string
}

const TermsheetAgreement = ({
  active,
  handleOnClose,
  opportunity,
  investmentAmount,
  investmentId,
}: TermsheetAgreementProps) => {
  const { authToken, name } = useSelector((state: RootState) => state.investor)
  const [isChecked, setChecked] = useState<boolean>(false)
  const [template, setTemplate] = useState<string>('')
  const [isTermsheetSigningPending, setIsTermsheetSigningPending] = useState(false)
  const [downloadLink, setDownloadLink] = useState<string | null>(null)
  const [termsheetResignMessage, setTermsheetResignMessage] = useState<string | undefined>()
  const [termsheetSigningLink, setTermsheetSigningLink] = useState<string | null>(null)
  const [showTermsheetSigningModal, setShowTermsheetSigningModal] = useState(false)
  const termsheetPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const closeTermsheetSigningModal = () => {
    setShowTermsheetSigningModal(false)
    setTermsheetSigningLink(null)
  }

  const stopTermsheetPolling = () => {
    if (termsheetPollRef.current) {
      clearInterval(termsheetPollRef.current)
      termsheetPollRef.current = null
    }
    setIsTermsheetSigningPending(false)
  }

  const applyTermsheetStateFromPoll = (
    result: ReturnType<typeof parseTermsheetAgreementPollResponse>,
  ) => {
    setTermsheetResignMessage(result.resignMessage)

    if (result.canContinue) {
      setChecked(true)
      if (result.downloadLink) {
        setDownloadLink(result.downloadLink)
      }
      closeTermsheetSigningModal()
      return
    }

    // Close signing UI as soon as signed/COMPLETED even if not fully ready yet
    if (
      !result.requiresResign &&
      (result.signed === true || result.status?.toUpperCase() === 'COMPLETED')
    ) {
      closeTermsheetSigningModal()
    }

    setChecked(false)
    setDownloadLink(null)
  }

  const pollTermsheetStatus = async () => {
    const res = await investServices.getSignedTermsheetAgreementDocument(
      opportunity?.id.toString() ?? '',
      investmentId,
      { Authorization: authToken ? authToken : '' },
    )
    const result = parseTermsheetAgreementPollResponse(res)
    applyTermsheetStateFromPoll(result)
    if (!result.canContinue) return false

    stopTermsheetPolling()
    logCrmEvent(
      EngagementEvents.INVESTOR_ACCEPTED_TERMSHEET_AGREEMENT,
      { Authorization: authToken ? authToken : '' },
      {
        opportunityName: opportunity?.projectName,
        opportunityId: opportunity?.id,
      },
    )
    setTimeout(() => handleOnClose(), 1000)
    return true
  }

  const handleModalBgClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && isChecked) {
      handleOnClose()
    }
  }

  const handleCheckboxClick = async () => {
    if (!investmentId) return

    // Mint a fresh link on every open — SignNow links expire and are effectively single-use
    const response = await documentServices.createTermsheetSigningLink(
      opportunity?.id.toString() ?? '',
      investmentAmount,
      investmentId,
      { Authorization: authToken ? authToken : '' },
    )
    const signingLink =
      response?.data?.signingLink ??
      (response?.data as { data?: { signingLink?: string } } | undefined)?.data?.signingLink
    if (response?.status !== 200 || !signingLink) return

    setTermsheetSigningLink(signingLink)
    setShowTermsheetSigningModal(true)
    setIsTermsheetSigningPending(true)
  }

  // Poll while signing iframe is visible — do not wait for SignNow redirect
  useEffect(() => {
    if (!termsheetSigningLink) return

    void pollTermsheetStatus()

    termsheetPollRef.current = setInterval(() => {
      void pollTermsheetStatus()
    }, TERMSHEET_POLL_INTERVAL_MS)

    return () => {
      if (termsheetPollRef.current) {
        clearInterval(termsheetPollRef.current)
        termsheetPollRef.current = null
      }
    }
  }, [termsheetSigningLink])

  const handleDownloadSignedTermsheet = () => {
    if (!downloadLink) return
    window.open(downloadLink)
  }

  const handleDownloadTermsheetAgreementDocumentPDF = async () => {
    if (!template) return

    await exportTermsheetHtmlToPdf({
      html: template,
      fileName: buildTermsheetPdfFileName(name, opportunity.projectName),
      sourceElement: contentRef.current,
      signatureImageUrl: TERMSHEET_SIGNATURE_IMAGE_URL,
    })
  }
  useEffect(() => {
    if (!opportunity?.id || !investmentId) return

    const syncTermsheetState = async () => {
      const res = await investServices.getSignedTermsheetAgreementDocument(
        opportunity.id.toString(),
        investmentId,
        { Authorization: authToken ? authToken : '' },
      )
      applyTermsheetStateFromPoll(parseTermsheetAgreementPollResponse(res))
    }

    void syncTermsheetState()
  }, [opportunity?.id, investmentAmount, investmentId, authToken])

  useEffect(() => {
    if (!opportunity?.id || !investmentId) return

    const getTemplate = async () => {
      const response = await documentServices.getTermsheetAgreementTemplate(
        opportunity.id.toString(),
        investmentAmount,
        investmentId,
        {
          Authorization: authToken ? authToken : '',
        },
      )
      setTemplate(response.data)
    }
    getTemplate()
  }, [opportunity?.id, investmentAmount, investmentId, authToken])

  useEffect(() => {
    if (active && !isChecked) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = 'auto'
  }, [active, isChecked])

  return active ? (
    <>
      <InputModal testId='termsheet-agreement-modal'>
        <div
          data-testid='termsheet-agreement-modal-card-container'
          onClick={handleModalBgClick}
          className='modal-card-container'
        >
          <Card classes={['p-24 transaction-checklist-modal']}>
            <Flex>
              <div className='payment-warning-container-fit-content mt-10'>
                <PendingIcon classes={['info-icon']} width='24px' height='24px' />
                {isTermsheetSigningPending
                  ? 'Processing signature...'
                  : termsheetResignMessage ??
                    'If you don’t see the updated investment amount, please refresh the page.'}
              </div>
            </Flex>
            {/* termsheet agreement html template */}
            <div
              className='self-declaration-wrapper self-declaration-wrapper-overflow mt-4 mb-24'
              style={{ maxHeight: 'calc(100% - 110px)' }}
              ref={contentRef}
            >
              {template && (
                <div
                  className='self-declaration-wrapper-text'
                  dangerouslySetInnerHTML={{ __html: template }}
                />
              )}
            </div>
            <Flex variant='space-between'>
              <TermsheetAgreementForm
                handleCheckboxClick={handleCheckboxClick}
                isChecked={isChecked}
                isProcessing={isTermsheetSigningPending}
              />
              <div>
                {isChecked && downloadLink && (
                  <a
                    className='basic-anchor mr-16'
                    onClick={handleDownloadSignedTermsheet}
                    data-testid='termsheet-agreement-download-signed'
                  >
                    Download signed termsheet
                  </a>
                )}
                <a className='basic-anchor' onClick={handleDownloadTermsheetAgreementDocumentPDF}>
                  Download Document
                </a>
              </div>
            </Flex>
          </Card>
        </div>
      </InputModal>
      {showTermsheetSigningModal && termsheetSigningLink ? (
        <SignNowEmbedModal
          isOpen
          signingLink={termsheetSigningLink}
          onClose={closeTermsheetSigningModal}
          title='Sign termsheet'
          onSigningComplete={() => {
            closeTermsheetSigningModal()
            void pollTermsheetStatus()
          }}
        />
      ) : null}
    </>
  ) : null
}

export { TermsheetAgreement }
